/**
 * HTTP 客户端 —— docs/04 §1 的统一约定在此落地。
 * 只用原生 fetch，不引 axios：拦截器/取消/流式在 fetch 上都能直接表达，多一层封装反而遮蔽 SSE 的行为。
 */

import type { ApiError, Page } from '@/types/api';

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/v1';

const API_KEY_STORAGE_KEY = 'aigzl.api_key';
const TOKEN_STORAGE_KEY = 'aigzl.session_token';

/** 运行时可改的 API Key：环境变量作为默认值，设置页写入 localStorage 后覆盖 */
export function getApiKey(): string {
  return localStorage.getItem(API_KEY_STORAGE_KEY) ?? import.meta.env.VITE_API_KEY ?? '';
}

export function setApiKey(key: string): void {
  if (key) localStorage.setItem(API_KEY_STORAGE_KEY, key);
  else localStorage.removeItem(API_KEY_STORAGE_KEY);
}

/**
 * 登录会话令牌。
 *
 * 存 localStorage 而不是 httpOnly Cookie，是一个**明确的取舍**：
 * - Cookie 更能抗 XSS（JS 读不到），但要处理 CSRF、SameSite、跨域携带凭证，
 *   而本项目的前后端是分离部署的（开发期 5173 → 8000，生产期常见反代到不同路径）；
 * - localStorage 下 XSS 能偷走令牌 —— 缓解手段是会话可撤销（服务端存会话表，
 *   退出/改密立刻失效）+ 有限有效期，而不是假装这个风险不存在。
 * 若将来把控制台与 API 同源部署，换成 httpOnly Cookie 是值得做的升级。
 */
export function getAuthToken(): string {
  return localStorage.getItem(TOKEN_STORAGE_KEY) ?? '';
}

export function setAuthToken(token: string): void {
  if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
  else localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * 服务端错误的前端载体。
 * 网络层失败（DNS/断网/CORS）也归一到这里，code 为 NETWORK_ERROR，
 * 这样调用方只需 catch 一种类型。
 */
export class ApiRequestError extends Error {
  readonly code: string;
  readonly requestId: string;
  readonly status: number;
  readonly detail: Record<string, unknown> | undefined;

  constructor(args: {
    code: string;
    message: string;
    requestId: string;
    status: number;
    detail?: Record<string, unknown>;
  }) {
    super(args.message);
    this.name = 'ApiRequestError';
    this.code = args.code;
    this.requestId = args.requestId;
    this.status = args.status;
    this.detail = args.detail;
  }
}

export function isApiRequestError(e: unknown): e is ApiRequestError {
  return e instanceof ApiRequestError;
}

/** docs/04 §1.1 的错误体形状判定，收窄 unknown 而非断言 */
function isApiErrorBody(body: unknown): body is ApiError {
  if (typeof body !== 'object' || body === null || !('error' in body)) return false;
  const err = (body as { error: unknown }).error;
  return typeof err === 'object' && err !== null && 'code' in err && 'message' in err;
}

function newRequestId(): string {
  // 与服务端 trace_id 对齐用，只要求全局唯一，不要求 ULID 格式
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 未授权时的全局回调，由 App 层注册（跳设置页/弹窗），避免 client 直接依赖路由 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/** SSE 走的是自己的 fetch 路径，401 时需要能触发同一个全局处理 */
export function notifyUnauthorized(): void {
  onUnauthorized?.();
}

/**
 * 鉴权头。普通请求与 SSE 共用，保证「凭证从哪来」只有一处答案。
 *
 * 会话令牌优先于 API Key：登录过就以登录身份行事。两者共用 Bearer，
 * 服务端先查会话表、查不到再比 Key（见 api/deps.py）。
 */
export function authHeaders(): Record<string, string> {
  const credential = getAuthToken() || getApiKey();
  return credential ? { Authorization: `Bearer ${credential}` } : {};
}

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** 写操作幂等键，docs/04 §1 */
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    // 空串也要丢掉：cursor='' 会被后端当成一个真实游标
    if (v === undefined || v === null || v === '') continue;
    qs.append(k, String(v));
  }
  const suffix = qs.toString();
  return `${API_BASE}${path}${suffix ? `?${suffix}` : ''}`;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const requestId = newRequestId();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': requestId,
    ...opts.headers,
  };

  Object.assign(headers, authHeaders());
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  let payload: string | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json; charset=utf-8';
    payload = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body: payload,
      signal: opts.signal,
    });
  } catch (e) {
    // AbortError 原样抛出：调用方靠它区分「用户切走了」和「真的失败了」
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiRequestError({
      code: 'NETWORK_ERROR',
      message: e instanceof Error ? e.message : '网络请求失败',
      requestId,
      status: 0,
    });
  }

  // 服务端回的 X-Request-Id 才是日志里能查到的那个
  const serverRequestId = res.headers.get('X-Request-Id') ?? requestId;

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    if (res.status === 401) onUnauthorized?.();
    if (isApiErrorBody(parsed)) {
      throw new ApiRequestError({
        code: parsed.error.code,
        message: parsed.error.message,
        requestId: parsed.error.request_id || serverRequestId,
        status: res.status,
        detail: parsed.error.detail,
      });
    }
    // 网关/代理返回的非 JSON 错误页也要能显示出来
    throw new ApiRequestError({
      code: `HTTP_${res.status}`,
      message: text.slice(0, 300) || res.statusText || '请求失败',
      requestId: serverRequestId,
      status: res.status,
    });
  }

  return parsed as T;
}

export const http = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  del: <T>(path: string, opts?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};

// ---------------------------------------------------------------- 游标分页

export interface CursorQuery {
  limit?: number;
  cursor?: string | null;
}

export const DEFAULT_PAGE_SIZE = 20;

/**
 * docs/04 §1.2：cursor 是上一页最后一条的 ULID，不是 offset。
 * 这里只负责把 cursor/limit 拼进 query 并返回统一的 Page<T>。
 */
export function getPage<T>(
  path: string,
  params: Record<string, QueryValue> & CursorQuery,
  signal?: AbortSignal,
): Promise<Page<T>> {
  const { limit = DEFAULT_PAGE_SIZE, cursor, ...rest } = params;
  return request<Page<T>>(path, {
    method: 'GET',
    query: { ...rest, limit, cursor: cursor ?? undefined },
    signal,
  });
}

/**
 * 把游标分页拉平成一次性全量（用于下拉框这类小数据集）。
 * 硬上限防止后端 has_more 永真时把浏览器拖死。
 */
export async function getAllPages<T>(
  path: string,
  params: Record<string, QueryValue> = {},
  maxPages = 20,
): Promise<T[]> {
  const all: T[] = [];
  let cursor: string | null = null;
  for (let i = 0; i < maxPages; i++) {
    const page: Page<T> = await getPage<T>(path, { ...params, limit: 100, cursor });
    all.push(...page.items);
    if (!page.has_more || !page.next_cursor) break;
    cursor = page.next_cursor;
  }
  return all;
}
